import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Crown, MoreHorizontal, UserPlus, Users } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { api } from "../../api/client";
import { Callout } from "../../components/Callout";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Menu, MenuItem, MenuSeparator } from "../../components/Menu";
import { Motion, Presence } from "../../components/presence";
import { Badge, Button, IconButton, Tabs } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { useSession } from "../auth";
import { authDate, authDateTime } from "../authText";
import { AccountDialog, BatchAccountsDialog, DeactivateDialog, ItemsDialog } from "./AccountForms";
import { CodeRevealDialog } from "./AdminDialogs";
import { adminErrorText, requestAction, roleLabel, sizeLabel, statusLabel } from "./adminText";
import type { AccountsList, Approval, CreatedAccount, IssuedCode, PendingFiled, RequestKind, UnitAccount } from "./adminTypes";
import type { Slip } from "./handover";
import { ReauthCancelled, withReauth } from "./reauth";
import { RequestsPanel } from "./RequestsPanel";
import { AuditTab, SessionsTab, SettingsTab } from "./UnitTabs";
import { UnitItemsDialog } from "../../sharing/UnitItemsDialog";
import { Waiting } from "../../components/Waiting";

/** « Comptes de l'unité » (docs/17 §5, §8.3) : vue réservée aux administrateurs, ouverte depuis le menu du compte.
 *  Onglets Comptes, Sessions, Journal, Réglages ; demandes à approuver en tête des comptes ; bandeaux du seul ou des
 *  deux seuls administrateurs et de l'usage récent du code de secours. Les codes rendus par le serveur ne vivent que
 *  dans la fenêtre qui les montre. */

export const ACCOUNTS_QUERY = ["unit-accounts"] as const;

type Tab = "accounts" | "sessions" | "log" | "settings";
type Message = { tone: "ok" | "danger" | "info"; text: string };
type Reveal = { slips: Slip[]; kind: "activation" | "reset" };
type Gesture = "reset-code" | "activation-code" | "lock" | "unlock" | "clear-delay" | "end-sessions" | "reactivate";

function slipOf(code: IssuedCode): Slip {
  return { name: code.account.display_name, login: code.account.login, code: code.code, expiresAt: code.expires_at };
}

function isPending(result: unknown): result is PendingFiled {
  return typeof result === "object" && result !== null && (result as PendingFiled).pending === true;
}

function isCode(result: unknown): result is IssuedCode {
  return typeof result === "object" && result !== null && typeof (result as IssuedCode).code === "string";
}

export function AccountsView({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const session = useSession().data;
  const unitName = session?.unit_name ?? null;
  const list = useQuery({ queryKey: ACCOUNTS_QUERY, queryFn: api.accounts.list, staleTime: 0, refetchInterval: 60_000 });
  const [tab, setTab] = useState<Tab>("accounts");
  const [message, setMessage] = useState<Message | null>(null);
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [recovery, setRecovery] = useState<string | null>(null);
  const [editing, setEditing] = useState<UnitAccount | "new" | null>(null);
  const [batch, setBatch] = useState(false);
  const [deactivating, setDeactivating] = useState<UnitAccount | null>(null);
  const [itemsOf, setItemsOf] = useState<UnitAccount | null>(null);
  // Éléments de l'unité sans propriétaire (lot 5, docs/17 §5.6) : « Attribuer un propriétaire… ».
  const [unitItems, setUnitItems] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const say = useCallback((next: Message) => {
    setMessage(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMessage(null), 8000);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY });
    void queryClient.invalidateQueries({ queryKey: ["unit-requests"] });
  }, [queryClient]);

  /** Demande à deux administrateurs déposée : qui doit l'approuver, et dans quel délai. */
  const filed = useCallback(
    (pending: PendingFiled, name: string) =>
      say({
        tone: "info",
        text: t("{action} pour {nom} : un second administrateur, autre que vous et que {nom}, doit l'approuver dans les 24 h. Sans lui, adressez-vous à BELLICOM.", {
          action: requestAction(pending.kind),
          nom: name,
        }),
      }),
    [say],
  );

  const act = useMutation({
    mutationFn: ({ account, gesture }: { account: UnitAccount; gesture: Gesture }) => withReauth(() => api.accounts.act(account.id, gesture)),
    onSuccess: (result, { account, gesture }) => {
      refresh();
      if (isPending(result)) return filed(result, account.display_name);
      if (isCode(result)) return setReveal({ slips: [slipOf(result)], kind: gesture === "reset-code" ? "reset" : "activation" });
      say({ tone: "ok", text: t("Compte de {nom} mis à jour", { nom: account.display_name }) });
    },
    onError: (error) => {
      if (!(error instanceof ReauthCancelled)) say({ tone: "danger", text: adminErrorText(error) });
      refresh();
    },
  });
  const setRole = useMutation({
    mutationFn: ({ account, role }: { account: UnitAccount; role: "admin" | "member" }) =>
      withReauth(() => api.accounts.update(account.id, { version: account.version, role })),
    onSuccess: (result, { account }) => {
      refresh();
      if (isPending(result)) return filed(result, account.display_name);
      say({ tone: "ok", text: t("Compte de {nom} mis à jour", { nom: account.display_name }) });
    },
    onError: (error) => {
      if (!(error instanceof ReauthCancelled)) say({ tone: "danger", text: adminErrorText(error) });
      refresh();
    },
  });

  const data: AccountsList | undefined = list.data;
  const accounts = data?.accounts ?? [];
  const me = data?.me ?? "";
  const admins = data?.admins ?? 0;
  // Accord du pluriel : « 1 compte · 1 administrateur ».
  const subtitle = data
    ? `${data.total === 1 ? t("1 compte") : t("{n} comptes", { n: data.total })} · ${
        admins === 1 ? t("1 administrateur") : t("{k} administrateurs", { k: admins })
      }`
    : undefined;

  const created = (result: CreatedAccount) => {
    setEditing(null);
    refresh();
    setReveal({ slips: [slipOf(result)], kind: "activation" });
    if (result.request) filed(result.request, result.account.display_name);
  };
  const approved = (approval: Approval) => {
    refresh();
    if (approval.code && approval.account)
      setReveal({
        slips: [{ name: approval.account.display_name, login: approval.account.login, code: approval.code, expiresAt: approval.expires_at ?? null }],
        kind: "reset",
      });
    else say({ tone: "ok", text: t("Demande approuvée") });
  };

  return (
    <>
      <Dialog title={t("Comptes de l'unité")} subtitle={subtitle} width={1080} onClose={onClose}>
        <div className="sticky top-0 z-10 border-b border-line bg-surface px-4 py-2">
          <Tabs<Tab>
            ariaLabel={t("Comptes de l'unité")}
            value={tab}
            onChange={setTab}
            tabs={[
              { value: "accounts", label: t("Comptes") },
              { value: "sessions", label: t("Sessions") },
              { value: "log", label: t("Journal") },
              { value: "settings", label: t("Réglages") },
            ]}
          />
        </div>
        <DialogBody>
          <Presence show={message !== null}>
            {message && (
              <Callout tone={message.tone} onClose={() => setMessage(null)}>
                {message.text}
              </Callout>
            )}
          </Presence>
          {list.error ? (
            <Callout tone="danger">{adminErrorText(list.error)}</Callout>
          ) : tab === "accounts" ? (
            <AccountsTab
              data={data}
              unitBusy={act.isPending || setRole.isPending}
              onNew={() => setEditing("new")}
              onBatch={() => setBatch(true)}
              onEdit={setEditing}
              onItems={setItemsOf}
              onUnitItems={() => setUnitItems(true)}
              onDeactivate={setDeactivating}
              onGesture={(account, gesture) => act.mutate({ account, gesture })}
              onRole={(account, role) => setRole.mutate({ account, role })}
              onBlocked={(text) => say({ tone: "info", text })}
              requests={<RequestsPanel onApproved={approved} onMessage={say} />}
            />
          ) : tab === "sessions" ? (
            <SessionsTab onMessage={say} />
          ) : tab === "log" ? (
            <AuditTab accounts={accounts} />
          ) : (
            <SettingsTab onMessage={say} onRecovery={setRecovery} />
          )}
        </DialogBody>
      </Dialog>
      <Presence show={editing !== null}>
        {editing !== null && (
          <AccountDialog
            account={editing === "new" ? null : editing}
            admins={admins}
            onCreated={created}
            onSaved={() => {
              setEditing(null);
              refresh();
              say({ tone: "ok", text: t("Compte enregistré") });
            }}
            onClose={() => setEditing(null)}
          />
        )}
      </Presence>
      <Presence show={batch}>
        {batch && (
          <BatchAccountsDialog
            onCreated={(codes) => {
              setBatch(false);
              refresh();
              setReveal({ slips: codes.map(slipOf), kind: "activation" });
            }}
            onClose={() => setBatch(false)}
          />
        )}
      </Presence>
      <Presence show={deactivating !== null}>
        {deactivating && (
          <DeactivateDialog
            account={deactivating}
            accounts={accounts}
            me={me}
            onDone={(result) => {
              const name = deactivating.display_name;
              const moved = deactivating.status === "disabled";
              setDeactivating(null);
              refresh();
              if (isPending(result)) filed(result, name);
              else if (moved) say({ tone: "ok", text: t("Éléments de {nom} transférés", { nom: name }) });
              else say({ tone: "ok", text: t("Compte de {nom} désactivé", { nom: name }) });
            }}
            onClose={() => setDeactivating(null)}
          />
        )}
      </Presence>
      <Presence show={itemsOf !== null}>{itemsOf && <ItemsDialog account={itemsOf} onClose={() => setItemsOf(null)} />}</Presence>
      <Presence show={unitItems}>{unitItems && <UnitItemsDialog onClose={() => setUnitItems(false)} />}</Presence>
      <Presence show={reveal !== null}>
        {reveal && <CodeRevealDialog slips={reveal.slips} kind={reveal.kind} unit={unitName} onClose={() => setReveal(null)} />}
      </Presence>
      <Presence show={recovery !== null}>{recovery && <RecoveryRevealDialog code={recovery} onClose={() => setRecovery(null)} />}</Presence>
    </>
  );
}

function AccountsTab({
  data,
  unitBusy,
  onNew,
  onBatch,
  onEdit,
  onItems,
  onUnitItems,
  onDeactivate,
  onGesture,
  onRole,
  onBlocked,
  requests,
}: {
  data: AccountsList | undefined;
  unitBusy: boolean;
  onNew: () => void;
  onBatch: () => void;
  onEdit: (account: UnitAccount) => void;
  onItems: (account: UnitAccount) => void;
  onUnitItems: () => void;
  onDeactivate: (account: UnitAccount) => void;
  onGesture: (account: UnitAccount, gesture: Gesture) => void;
  onRole: (account: UnitAccount, role: "admin" | "member") => void;
  onBlocked: (text: string) => void;
  requests: ReactNode;
}) {
  if (!data) return <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>;
  const { admins, recovery_used: recovery } = data;
  return (
    <Motion origin="fade" className="space-y-3">
      {admins <= 1 && (
        <Callout tone="warn">
          {t("Vous êtes le seul administrateur de l'unité. Créez le compte d'un deuxième administrateur, puis demandez sa nomination à BELLICOM, qui vérifiera son identité : sans administrateur, plus personne ne peut créer de compte ni remettre de code.")}
        </Callout>
      )}
      {admins === 2 && (
        <Callout tone="info">
          {t("Nommez un troisième administrateur : réinitialiser le compte d'un administrateur demande l'accord d'un administrateur qui n'est ni le demandeur ni la cible.")}
        </Callout>
      )}
      {recovery && (
        <Callout tone="warn">
          {t("Code de secours utilisé le {date} sur le compte de {nom}, depuis le poste « {poste} ».", {
            date: authDate(recovery.at),
            nom: recovery.name ?? "—",
            poste: recovery.seat ?? "—",
          })}
        </Callout>
      )}
      {requests}
      <div className="flex flex-wrap gap-2">
        <Button onClick={onNew}>
          <UserPlus size={14} /> {t("Nouveau compte")}
        </Button>
        <Button variant="secondary" onClick={onBatch}>
          <Users size={14} /> {t("Plusieurs comptes")}
        </Button>
        <Button variant="secondary" onClick={onUnitItems}>
          <Crown size={14} /> {t("Éléments de l'unité…")}
        </Button>
      </div>
      <div className="hidden grid-cols-[1.4fr_1fr_1fr_auto_1fr_auto_auto_auto_auto] gap-x-3 border-b border-line pb-1.5 text-meta font-medium text-ink-muted lg:grid">
        <span>{t("Grade et nom")}</span>
        <span>{t("Fonction")}</span>
        <span>{t("Identifiant")}</span>
        <span>{t("Rôle")}</span>
        <span>{t("État")}</span>
        <span>{t("Dernière connexion")}</span>
        <span>{t("Éléments")}</span>
        <span>{t("Espace occupé")}</span>
        <span className="w-8" />
      </div>
      <ul>
        {data.accounts.map((account) => (
          <AccountRow
            key={account.id}
            account={account}
            mine={account.id === data.me}
            admins={admins}
            busy={unitBusy}
            onEdit={onEdit}
            onItems={onItems}
            onDeactivate={onDeactivate}
            onGesture={onGesture}
            onRole={onRole}
            onBlocked={onBlocked}
          />
        ))}
      </ul>
    </Motion>
  );
}

function AccountRow({
  account,
  mine,
  admins,
  busy,
  onEdit,
  onItems,
  onDeactivate,
  onGesture,
  onRole,
  onBlocked,
}: {
  account: UnitAccount;
  mine: boolean;
  admins: number;
  busy: boolean;
  onEdit: (account: UnitAccount) => void;
  onItems: (account: UnitAccount) => void;
  onDeactivate: (account: UnitAccount) => void;
  onGesture: (account: UnitAccount, gesture: Gesture) => void;
  onRole: (account: UnitAccount, role: "admin" | "member") => void;
  onBlocked: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const disabled = account.status === "disabled";
  const items = account.items;
  const count = items.exercises + items.odbs + items.zones;
  const label = t("Actions pour {nom}", { nom: account.display_name });
  const tone = account.status === "active" ? (account.delayed_until ? "warn" : "ok") : account.status === "disabled" ? "neutral" : "warn";
  return (
    <li
      className={clsx(
        "grid min-h-10 grid-cols-[1fr_auto] items-center gap-x-3 border-b border-line py-2 last:border-b-0 lg:grid-cols-[1.4fr_1fr_1fr_auto_1fr_auto_auto_auto_auto]",
        disabled && "text-ink-muted",
      )}
    >
      <span className="min-w-0">
        <span className="block truncate text-ui font-medium text-ink">
          {account.display_name}
          {mine && <span className="ml-1.5 font-normal text-ink-muted">{t("(vous)")}</span>}
        </span>
        <span className="block truncate text-meta text-ink-muted lg:hidden">
          {[account.appointment, account.login, roleLabel(account.role), statusLabel(account)].filter(Boolean).join(" · ")}
        </span>
        {account.pending_requests.length > 0 && (
          <span className="mt-0.5 block text-meta text-ink-muted">
            {t("Demande en attente : {action}", { action: account.pending_requests.map((kind) => requestAction(kind as RequestKind)).join(", ") })}
          </span>
        )}
      </span>
      <span className="hidden min-w-0 truncate text-ui text-ink-soft lg:block">{account.appointment || "—"}</span>
      <span className="hidden min-w-0 truncate font-mono text-meta text-ink-soft lg:block">{account.login || "—"}</span>
      <span className="hidden lg:block">
        {account.role === "admin" ? <Badge tone="accent">{roleLabel(account.role)}</Badge> : <span className="text-ui text-ink-soft">{roleLabel(account.role)}</span>}
      </span>
      <span className="hidden lg:block">
        <Badge tone={tone} dot={account.status !== "active" || Boolean(account.delayed_until)}>
          {statusLabel(account)}
        </Badge>
      </span>
      <span className="hidden text-meta text-ink-muted tabular-nums lg:block">{authDateTime(account.last_login_at)}</span>
      <span className="hidden text-right text-meta text-ink-muted tabular-nums lg:block">{count}</span>
      <span className="hidden text-right text-meta text-ink-muted tabular-nums lg:block">{sizeLabel(items.bytes)}</span>
      <span className="flex justify-end">
        <Menu
          open={open}
          onClose={close}
          portal
          align="end"
          width={260}
          ariaLabel={label}
          trigger={
            <IconButton onClick={() => setOpen((o) => !o)} title={label} aria-label={label} disabled={busy}>
              <MoreHorizontal size={16} />
            </IconButton>
          }
        >
          <RowItems
            account={account}
            mine={mine}
            admins={admins}
            onEdit={onEdit}
            onItems={onItems}
            onDeactivate={onDeactivate}
            onGesture={onGesture}
            onRole={onRole}
            onBlocked={onBlocked}
          />
        </Menu>
      </span>
    </li>
  );
}

function RowItems({
  account,
  mine,
  admins,
  onEdit,
  onItems,
  onDeactivate,
  onGesture,
  onRole,
  onBlocked,
}: {
  account: UnitAccount;
  mine: boolean;
  admins: number;
  onEdit: (account: UnitAccount) => void;
  onItems: (account: UnitAccount) => void;
  onDeactivate: (account: UnitAccount) => void;
  onGesture: (account: UnitAccount, gesture: Gesture) => void;
  onRole: (account: UnitAccount, role: "admin" | "member") => void;
  onBlocked: (text: string) => void;
}) {
  if (account.status === "disabled") {
    return (
      <>
        <MenuItem label={t("Éléments…")} onSelect={() => onItems(account)} />
        <MenuItem
          label={t("Transférer ses éléments…")}
          blocked={account.items.exercises + account.items.odbs + account.items.zones === 0}
          onBlocked={() => onBlocked(t("Ce compte ne possède aucun élément."))}
          onSelect={() => onDeactivate(account)}
        />
        <MenuItem label={t("Réactiver")} onSelect={() => onGesture(account, "reactivate")} />
      </>
    );
  }
  const pending = account.status === "pending";
  const lastAdmin = account.role === "admin" && admins <= 1;
  return (
    <>
      <MenuItem label={t("Modifier")} onSelect={() => onEdit(account)} />
      <MenuItem label={t("Éléments…")} onSelect={() => onItems(account)} />
      <MenuSeparator />
      {pending ? (
        <MenuItem label={t("Nouveau code d'activation")} onSelect={() => onGesture(account, "activation-code")} />
      ) : (
        <MenuItem
          label={t("Code de réinitialisation")}
          blocked={mine}
          onBlocked={() => onBlocked(t("Pour votre propre compte, changez votre mot de passe depuis le menu de votre compte (« Changer de mot de passe »)."))}
          onSelect={() => onGesture(account, "reset-code")}
        />
      )}
      {account.status === "locked" ? (
        <MenuItem label={t("Déverrouiller")} onSelect={() => onGesture(account, "unlock")} />
      ) : (
        !mine && <MenuItem label={t("Verrouiller")} onSelect={() => onGesture(account, "lock")} />
      )}
      <MenuItem label={t("Lever le délai d'attente")} onSelect={() => onGesture(account, "clear-delay")} />
      {!mine && <MenuItem label={t("Fermer ses sessions")} onSelect={() => onGesture(account, "end-sessions")} />}
      <MenuSeparator />
      {account.role === "admin" ? (
        <MenuItem
          label={t("Retirer le rôle d'administrateur")}
          blocked={lastAdmin}
          onBlocked={() => onBlocked(t("L'unité doit garder au moins un administrateur actif : nommez-en un autre d'abord."))}
          onSelect={() => onRole(account, "member")}
        />
      ) : (
        <MenuItem
          label={t("Nommer administrateur")}
          blocked={admins < 2 || account.status !== "active"}
          onBlocked={() =>
            onBlocked(
              account.status !== "active"
                ? t("Ce compte doit d'abord être activé par la personne elle-même avec son code.")
                : t("Le deuxième administrateur de l'unité est nommé par BELLICOM, après vérification de son identité : demandez-le à BELLICOM en donnant l'identifiant de ce compte."),
            )
          }
          onSelect={() => onRole(account, "admin")}
        />
      )}
      {!mine && <MenuItem danger label={t("Désactiver…")} blocked={lastAdmin} onBlocked={() => onBlocked(t("L'unité doit garder au moins un administrateur actif : nommez-en un autre d'abord."))} onSelect={() => onDeactivate(account)} />}
    </>
  );
}

/** Nouveau code de secours, affiché une seule fois à celui qui le régénère. */
function RecoveryRevealDialog({ code, onClose }: { code: string; onClose: () => void }) {
  return (
    <Dialog
      title={t("Code de secours de l'unité")}
      width={440}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t("Fermer")}</Button>}
    >
      <DialogBody>
        <p className="text-ui leading-relaxed text-ink-soft">
          {t("Notez ce code et confiez-le au commandement de l'unité. Avec lui, un administrateur qui a perdu son mot de passe reprend la main sans aide extérieure, quand aucun autre administrateur n'est joignable. Il ne sera plus affiché.")}
        </p>
        <div className="rounded-lg border border-line bg-canvas px-3 py-3 text-center font-mono text-heading tracking-wider text-ink tabular-nums select-all">
          {code}
        </div>
      </DialogBody>
    </Dialog>
  );
}
